/**
 * Страница заполнения диагностики механиком.
 * 
 * Отвечает за:
 * - взятие заявки в работу
 * - заполнение чеклиста с фото
 * - запись голосового комментария или текстового
 * - проверку заключения механика
 * - завершение диагностики
 * 
 * Применяется: Mechanic
 */

import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useChecklist } from './hooks/useChecklist'
import { usePhotoUpload } from './hooks/usePhotoUpload'
import { useVoiceRecorder } from './hooks/useVoiceRecorder'
import MechanicConclusion from './MechanicConclusion'
import InspectionHints from '../../shared/ui/InspectionHints'

import PhotoPreviewOverlay from '../../shared/ui/PhotoPreviewOverlay'
import ClientEditModal from '../../features/client-edit/ClientEditModal'
import OrderHistory from '../order-view/OrderHistory'
import BackButton from '../../shared/ui/BackButton'
import { diagnosticStyles } from './diagnosticStyles'
import { orderViewStyles } from '../order-view/orderViewStyles'

const groupIcons = {
    'Двигатель и тех. жидкости': 'ti-engine',
    'Ходовая часть': 'ti-car',
    'Кузов и электрика': 'ti-bolt',
    'Дополнительный осмотр': 'ti-zoom-check',
}

const statusOptions = [
    { value: 'ok', label: 'Исправно', bg: '#059669', color: '#059669', icon: 'ti-circle-check' },
    { value: 'warning', label: 'Некритичное', bg: '#D97706', color: '#D97706', icon: 'ti-alert-circle' },
    { value: 'critical', label: 'Критичное', bg: '#DC2626', color: '#DC2626', icon: 'ti-alert-triangle' },
    { value: 'na', label: 'Н/П', bg: '#6B7280', color: '#6B7280', icon: 'ti-minus' },
]

// худший статус пункта — им красится полоска слева
const statusWeight = { critical: 4, warning: 3, ok: 2, na: 1 }

function PhotoBox({ missing = false, title, list, alt, onDelete, failed, retryLabel = 'Ошибка — повторить', onRetry, onGallery, onCamera, readOnly = false, onPreview }) {
    return (
        <div className="dg-photos" data-missing={missing}>
            <p className="dg-photos-title">
                <i className={`ti ${missing ? 'ti-alert-triangle' : 'ti-camera'}`} aria-hidden="true" />
                {title}
            </p>
            {failed && <button type="button" className="dg-retry" onClick={onRetry}>{retryLabel}</button>}
            {list?.length > 0 && (
                <div className="dg-thumbs">
                    {list.map((photo, i) => (
                        <div key={i} className="dg-thumb">
                            <img src={photo.url} alt={alt} onClick={() => onPreview(photo.url)} />
                            {!readOnly && <button type="button" aria-label="Удалить фото" onClick={() => onDelete(photo, i)}><i className="ti ti-x" aria-hidden="true" /></button>}
                        </div>
                    ))}
                </div>
            )}
            {!readOnly && (
                <div className="dg-photo-buttons">
                    <button type="button" className="dg-gallery" onClick={onGallery}><i className="ti ti-photo" aria-hidden="true" />Из галереи</button>
                    <button type="button" className="dg-camera" onClick={onCamera}><i className="ti ti-camera" aria-hidden="true" />Камера</button>
                </div>
            )}
        </div>
    )
}

export default function Diagnostic({ order, onBack, onComplete }) {
    const [currentOrder, setCurrentOrder] = useState(order)
    const [taking, setTaking] = useState(false)
    const [returning, setReturning] = useState(false)
    const [mileageInput, setMileageInput] = useState('')
    const [mileageConfirmed, setMileageConfirmed] = useState(false)
    const [savingMileage, setSavingMileage] = useState(false)
    const [missingPhotos, setMissingPhotos] = useState([])
    const [previewPhoto, setPreviewPhoto] = useState(null)
    const [summary, setSummary] = useState('')
    const [completingOrder, setCompletingOrder] = useState(false)
    const [isCompleted, setIsCompleted] = useState(false)
    const [completedOrder, setCompletedOrder] = useState(null)
    const [inspectionHints, setInspectionHints] = useState([])
    const [hintsError, setHintsError] = useState('')
    const [loadingHints, setLoadingHints] = useState(false)
    const [showClientEdit, setShowClientEdit] = useState(false)
    const mileagePhotoRef = useRef(null)
    const fileInputRefs = useRef({})

    const { token } = useAuth()

    const {
        photos, initPhotos,
        uploadPhoto, uploadMileagePhoto, uploadAdditionalPhoto, deletePhoto, getPhotos,
        failedUploads, retryUpload, hasUploadsInProgress,
    } = usePhotoUpload(token)

    const {
        items, results, loading,
        saveResult, saveMileage, validateChecklist, getStatus
    } = useChecklist(order.id, token, (resultsData, intakeData) => {
        const photosMap = {}
        resultsData.forEach(r => {
            const key = `${r.item_id}_${r.side}`
            if (r.photos && r.photos.length > 0) {
                photosMap[key] = r.photos.map(p => ({ id: p.id, url: p.path, storage: 'legacy' }))
            }
        })
        if (intakeData?.photos?.length) {
            photosMap['15_none'] = intakeData.photos.map(photo => ({
                id: photo.id,
                url: photo.path,
                storage: photo.storage || 'order',
            }))
        }
        if (intakeData?.additional_photos?.length) {
            photosMap.additional_none = intakeData.additional_photos.map(photo => ({
                id: photo.id,
                url: photo.path,
                storage: 'order',
            }))
        }
        setMileageInput(intakeData?.mileage == null ? '' : String(intakeData.mileage))
        setCurrentOrder(prev => ({ ...prev, mileage: intakeData?.mileage ?? null }))
        setMileageConfirmed(Boolean(intakeData?.mileage_confirmed_at))
        if (intakeData?.inspection_hints) setInspectionHints(intakeData.inspection_hints)
        initPhotos(photosMap)
    })

    const recording = useVoiceRecorder(token, currentOrder.id, (newTranscript) => {
        setSummary(newTranscript)
        setCurrentOrder(prev => ({
            ...prev,
            mechanic_voice_transcript: newTranscript,
            mechanic_conclusion: newTranscript,
        }))
    })
    const { isRecording, isStartingRecording, processingAudio, setAudioUrl } = recording

    useEffect(() => {
        setSummary(order.mechanic_conclusion || order.mechanic_voice_transcript || '')
        if (order.mechanic_audio_path) setAudioUrl(`/api/orders/${order.id}/audio?token=${token}`)
        if (order.inspection_hints) {
            setInspectionHints(order.inspection_hints)
        }
    }, [order.id, order.mechanic_conclusion, order.mechanic_voice_transcript, order.mechanic_audio_path, order.inspection_hints, setAudioUrl, token])

    useEffect(() => {
        if (isCompleted && completedOrder && onComplete) {
            onComplete(completedOrder)
        }
    }, [isCompleted, completedOrder, onComplete])

    const isReadOnly = ['completed', 'closed'].includes(currentOrder.status) || currentOrder.is_sent

    const takeOrder = async () => {
        setTaking(true)
        const res = await fetch(`/api/orders/${currentOrder.id}/take`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (res.ok) {
            setCurrentOrder(data)
        } else {
            alert(data.message || 'Диагностика уже взята в работу')
            onBack()
        }
        setTaking(false)
    }

    const handleSaveResult = async (itemId, side, status) => {
        if (isReadOnly || !checklistUnlocked) return
        const data = await saveResult(currentOrder.id, itemId, side, status)
        if (data) setMissingPhotos(prev => prev.filter(k => k !== `${itemId}_${side || 'none'}`))
        return data
    }

    const handleSaveMileage = async (value) => {
        setSavingMileage(true)
        try {
            const data = await saveMileage(currentOrder.id, value)
            setCurrentOrder(prev => ({ ...prev, mileage: data.mileage }))
            setMileageConfirmed(Boolean(data.mileage_confirmed_at))
            if (!data.mileage_confirmed_at) setInspectionHints([])
            setMissingPhotos(prev => prev.filter(k => k !== 'mileage' && k !== 'mileage_photo'))
            return data
        } finally {
            setSavingMileage(false)
        }
    }

    const confirmMileage = async () => {
        if (!mileageInput) return
        if (!photos['15_none']?.length) return
        setLoadingHints(true)
        setHintsError('')
        try {
            await handleSaveMileage(mileageInput)
            const confirmation = await fetch(`/api/orders/${currentOrder.id}/mileage/confirm`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            })
            const confirmationData = await confirmation.json()
            if (!confirmation.ok) throw new Error(confirmationData.message || 'Не удалось подтвердить пробег')
            setMileageConfirmed(true)
            const res = await fetch(`/api/orders/${currentOrder.id}/inspection-hints`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ mileage: mileageInput })
            })
            const data = await res.json()
            if (res.ok) setInspectionHints(data.hints || [])
            else setHintsError(data.message || 'Сервис подсказок временно недоступен')
        } catch (err) {
            setHintsError(err.message || 'Не удалось получить подсказки')
        } finally {
            setLoadingHints(false)
        }
    }

    const handleDeletePhoto = async (itemId, side, photoId, index) => {
        if (isReadOnly) return
        const deleted = await deletePhoto(itemId, side, photoId, index)
        if (!deleted) return
        if (itemId === 15) {
            setMileageConfirmed(false)
            setInspectionHints([])
        }
        if (itemId === 15 && mileagePhotoRef.current) mileagePhotoRef.current.value = ''
        if (fileInputRefs.current[`${itemId}_${side || 'none'}`]) {
            fileInputRefs.current[`${itemId}_${side || 'none'}`].value = ''
        }
    }

    const completeOrder = async () => {
        if (!conclusionUnlocked || isRecording || isStartingRecording || processingAudio || completingOrder || !summary.trim()) return
        const missing = validateChecklist(mileageInput, photos)
        if (missing.length > 0) {
            setMissingPhotos(missing)
            setTimeout(() => document.querySelector('[data-missing="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50)
            return
        }
        if (hasUploadsInProgress) {
            alert('Дождитесь завершения загрузки фотографий')
            return
        }
        setCompletingOrder(true)
        try {
            const saveResponse = await fetch(`/api/orders/${currentOrder.id}/summary`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ summary: summary.trim() })
            })
            const saveData = await saveResponse.json()
            if (!saveResponse.ok) throw new Error(saveData.message || 'Не удалось сохранить заключение')
            const res = await fetch(`/api/orders/${currentOrder.id}/complete`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}` }
            })
            const data = await res.json()
            if (res.ok) {
                setCompletedOrder({ ...currentOrder, ...data })
                setIsCompleted(true)
            } else {
                const missingKeys = (data.missing || []).map(item => {
                    if (item.type === 'mileage') return 'mileage'
                    if (item.type === 'odometer_photo') return 'mileage_photo'
                    if (item.itemId) return `${item.itemId}_${item.side || 'none'}`
                    return null
                }).filter(Boolean)
                if (missingKeys.length) setMissingPhotos(missingKeys)
                throw new Error(data.message || 'Не удалось завершить диагностику')
            }
        } catch (err) {
            alert(err.message || 'Ошибка завершения')
        } finally {
            setCompletingOrder(false)
        }
    }

    const canReturnOrder = currentOrder.status === 'in_progress'
        && !savingMileage
        && !hasUploadsInProgress
        && !currentOrder.mileage
        && !mileageInput
        && Object.keys(results).length === 0
        && Object.values(photos).every(list => !list?.length)

    const returnOrder = async () => {
        if (!canReturnOrder || returning) return
        setReturning(true)
        try {
            const response = await fetch(`/api/orders/${currentOrder.id}/return`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}` },
            })
            const data = await response.json()
            if (!response.ok) {
                alert(data.message || 'Не удалось вернуть заявку')
                return
            }
            onBack()
        } catch {
            alert('Ошибка соединения с сервером')
        } finally {
            setReturning(false)
        }
    }

    const isMissing = (itemId, side) => {
        const key = `${itemId}_${side || 'none'}`
        return missingPhotos.includes(key)
    }

    const getDoneCount = () => Object.keys(results).length
    const getTotalCount = () => {
        let total = 0
        items.forEach(item => { total += item.has_sides ? 2 : 1 })
        return total
    }

    const groups = [...new Set(items.map(i => i.group_name))]
    const done = getDoneCount()
    const total = getTotalCount()
    const progress = total > 0 ? Math.round((done / total) * 100) : 0
    const checklistUnlocked = Boolean(mileageInput) && mileageConfirmed && Boolean(photos['15_none']?.length) && !savingMileage && !loadingHints
    const requiredStatusKeys = items
        .filter(item => item.group_name !== 'Приёмка' && item.group_name !== 'Дополнительный осмотр')
        .flatMap(item => (item.has_sides ? ['left', 'right'] : ['none']).map(side => `${item.id}_${side}`))
    const remainingStatuses = requiredStatusKeys.filter(key => !statusOptions.some(option => option.value === results[key]?.status)).length
    const conclusionUnlocked = checklistUnlocked && requiredStatusKeys.length > 0 && remainingStatuses === 0

    const sidesOf = item => (item.has_sides ? ['left', 'right'] : [null])
    const itemState = item => sidesOf(item)
        .map(side => getStatus(item.id, side))
        .filter(Boolean)
        .sort((a, b) => (statusWeight[b] || 0) - (statusWeight[a] || 0))[0] || undefined
    const itemMissing = item => missingPhotos.includes(`${item.id}_none`) || missingPhotos.includes(`${item.id}_left`) || missingPhotos.includes(`${item.id}_right`)
    const groupCounts = group => {
        const keys = items.filter(i => i.group_name === group).flatMap(item => sidesOf(item).map(side => `${item.id}_${side || 'none'}`))
        return { done: keys.filter(key => results[key]).length, total: keys.length }
    }

    const statusButtons = (item, side) => (
        <div className="dg-status-grid">
            {statusOptions.map(opt => {
                const active = getStatus(item.id, side || 'none') === opt.value
                return (
                    <button key={opt.value} type="button" className="dg-status" data-tone={opt.value} aria-pressed={active} onClick={() => handleSaveResult(item.id, side, opt.value)} disabled={isReadOnly}>
                        <i className={`ti ${opt.icon}`} aria-hidden="true" />
                        {opt.label}
                    </button>
                )
            })}
        </div>
    )

    if (loading) return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F3F5F9' }}>
            <p style={{ color: '#6B7280' }}>Загрузка...</p>
        </div>
    )

    if (currentOrder.status === 'open') return (
        <div className="dg-take">
            <style>{diagnosticStyles}</style>
            <div className="dg-take-head">
                <div className="dg-wrap dg-head-inner">
                    <BackButton onClick={onBack} />
                    <span className="dg-badge" style={{ background: '#FFF4EF', color: '#FF7438' }}>Новая</span>
                </div>
            </div>
            <div className="dg-take-preview">
                <div className="dg-wrap dg-body">
                    <div className="dg-card">
                        <div className="dg-card-head">
                            <span className="dg-step"><i className="ti ti-gauge" aria-hidden="true" /></span>
                            <p className="dg-card-title">Пробег при приёмке</p>
                        </div>
                        <div className="dg-input" style={{ height: '42px' }} />
                    </div>
                    {groups.filter(g => g !== 'Приёмка' && g !== 'Дополнительный осмотр').map(group => (
                        <div key={group} className="dg-group">
                            <div className="dg-group-head">
                                <span className="dg-group-icon"><i className="ti ti-lock" aria-hidden="true" /></span>
                                <h2>{group}</h2>
                            </div>
                            {items.filter(i => i.group_name === group).map(item => (
                                <div key={item.id} className="dg-item">
                                    <p className="dg-item-title">{item.item_name}</p>
                                    <div className="dg-status-grid">
                                        {statusOptions.map(opt => (
                                            <div key={opt.value} className="dg-status" data-tone={opt.value}>{opt.label}</div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
            <div className="dg-take-shade" />
            <div className="dg-sheet">
                <div className="dg-sheet-grip" />
                <p className="dg-sheet-car">{currentOrder.car_brand} {currentOrder.car_model}</p>
                <p className="dg-sheet-sub">{currentOrder.plate && <span className="dg-plate">{currentOrder.plate}</span>}{currentOrder.client_name}</p>
                <div className="dg-sheet-grid">
                    {[
                        { label: 'Клиент', value: currentOrder.client_name },
                        { label: 'Телефон', value: currentOrder.client_phone },
                        { label: 'Гос. номер', value: currentOrder.plate },
                        { label: 'Год выпуска', value: currentOrder.car_year },
                        { label: '№ заказ-наряда', value: currentOrder.number || '—' },
                        { label: 'Создана', value: new Date(currentOrder.created_at).toLocaleDateString('ru-RU', { timeZone: 'Europe/Moscow' }) },
                    ].map(item => (
                        <div key={item.label} className="dg-sheet-field">
                            <p>{item.label}</p>
                            <b>{item.value}</b>
                        </div>
                    ))}
                </div>
                <button type="button" className="dg-take-btn" onClick={takeOrder} disabled={taking}>
                    {!taking && <i className="ti ti-player-play" aria-hidden="true" />}
                    {taking ? 'Загрузка...' : 'Взять в работу'}
                </button>
            </div>
        </div>
    )

    const renderPhotoSection = (itemId, side) => {
        const status = getStatus(itemId, side)
        if (!status) return null
        if (status === 'na') return null
        const itemPhotos = getPhotos(itemId, side)
        if (isReadOnly && itemPhotos.length === 0) return null
        const missing = isMissing(itemId, side)
        const refKey = `${itemId}_${side || 'none'}`
        const isOptional = status === 'ok'

        return (
            <>
                <input type="file" accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,image/*" ref={el => fileInputRefs.current[refKey] = el} style={{ display: 'none' }} onChange={async e => {
                    const file = e.target.files[0]
                    if (!file) return
                    let resultId = results[`${itemId}_${side || 'none'}`]?.id
                    if (!resultId) {
                        const data = await handleSaveResult(itemId, side, getStatus(itemId, side) || 'ok')
                        resultId = data?.id
                    }
                    if (resultId) uploadPhoto(itemId, side, file, resultId)
                }} />
                <PhotoBox
                    onPreview={setPreviewPhoto}
                    missing={missing}
                    title={missing ? 'Необходимо прикрепить фото' : isOptional ? 'Фото (необязательно)' : 'Фото дефекта'}
                    list={itemPhotos}
                    alt="фото дефекта"
                    readOnly={isReadOnly}
                    onDelete={(photo, i) => handleDeletePhoto(itemId, side, photo.id, i)}
                    failed={failedUploads[refKey]}
                    onRetry={() => retryUpload(refKey)}
                    onGallery={() => { if (fileInputRefs.current[refKey]) { fileInputRefs.current[refKey].removeAttribute('capture'); fileInputRefs.current[refKey].click() } }}
                    onCamera={() => { if (fileInputRefs.current[refKey]) { fileInputRefs.current[refKey].setAttribute('capture', 'camera'); fileInputRefs.current[refKey].click() } }}
                />
            </>
        )
    }

    const mileageMissing = missingPhotos.includes('mileage') || missingPhotos.includes('mileage_photo')
    const mileageReady = !hasUploadsInProgress && Boolean(mileageInput) && Boolean(photos['15_none']?.length)
    const checklistGroups = groups.filter(g => g !== 'Приёмка' && g !== 'Дополнительный осмотр')

    return (
        <div className="dg-page">
            <style>{diagnosticStyles + orderViewStyles}</style>

            {showClientEdit && (
                <ClientEditModal
                    order={currentOrder}
                    token={token}
                    onClose={() => setShowClientEdit(false)}
                    onSave={(updatedOrder) => setCurrentOrder(prev => ({ ...prev, ...updatedOrder }))}
                />
            )}

            <PhotoPreviewOverlay src={previewPhoto} onClose={() => setPreviewPhoto(null)} />

            <div className="dg-head">
                <div className="dg-wrap dg-head-inner">
                    <div className="dg-head-row">
                        <BackButton onClick={onBack} />
                        <div className="dg-head-title">
                            <p className="dg-car">{currentOrder.car_brand} {currentOrder.car_model}</p>
                            <p className="dg-sub">
                                {currentOrder.plate && <span className="dg-plate">{currentOrder.plate}</span>}
                                <span>{[currentOrder.car_year, currentOrder.client_name].filter(Boolean).join(' · ')}</span>
                                {currentOrder.client_phone && <a href={`tel:${String(currentOrder.client_phone).replace(/[^\d+]/g, '')}`}><i className="ti ti-phone" aria-hidden="true" />{currentOrder.client_phone}</a>}
                            </p>
                        </div>
                        <div className="dg-actions">
                            {canReturnOrder && (
                                <button type="button" className="dg-action" data-tone="grey" onClick={returnOrder} disabled={returning} aria-label="Вернуть" title="Вернуть заявку">
                                    <i className="ti ti-arrow-back-up" aria-hidden="true" />
                                    <span className="dg-action-label">{returning ? 'Возвращаем...' : 'Вернуть'}</span>
                                </button>
                            )}
                            {!isReadOnly && (
                                <button type="button" className="dg-action" data-tone="blue" onClick={() => setShowClientEdit(true)} aria-label="Изменить" title="Изменить данные клиента">
                                    <i className="ti ti-pencil" aria-hidden="true" />
                                    <span className="dg-action-label">Изменить</span>
                                </button>
                            )}
                            {isReadOnly && (
                                <span className="dg-badge" style={{ background: '#D1FAE5', color: '#059669' }}>Завершено</span>
                            )}
                        </div>
                    </div>
                    <div className="dg-progress">
                        <div className="dg-progress-bar" data-full={total > 0 && done >= total}><div style={{ width: `${progress}%` }} /></div>
                        <span>{done}/{total}</span>
                    </div>
                </div>
            </div>

            <div className="dg-wrap dg-body">

                {/* МАСТЕР / МЕХАНИК / САЛОН — как на экране закрытой заявки */}
                <div className="cv-info">
                    {[
                        currentOrder.master_name && { icon: 'ti-user', label: 'Мастер', value: currentOrder.master_name },
                        currentOrder.mechanic_name && { icon: 'ti-tool', label: 'Механик', value: currentOrder.mechanic_name },
                        currentOrder.salon_name && { icon: 'ti-building-store', label: 'Салон', value: currentOrder.salon_name },
                        currentOrder.in_progress_at && { icon: 'ti-player-play', label: 'В работе с', value: new Date(currentOrder.in_progress_at).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }), tone: 'amber' },
                        currentOrder.number && { icon: 'ti-file-invoice', label: '№ заказ-наряда', value: currentOrder.number },
                    ].filter(Boolean).slice(0, 4).map(field => (
                        <div key={field.label} className="cv-field">
                            <span className="cv-field-icon" data-tone={field.tone}><i className={`ti ${field.icon}`} aria-hidden="true" /></span>
                            <div><p>{field.label}</p><b>{field.value}</b></div>
                        </div>
                    ))}
                </div>

                {!isReadOnly && (
                    <section className="dg-card" data-missing={mileageMissing}>
                        <div className="dg-card-head">
                            <span className="dg-step" data-done={mileageConfirmed}><i className={`ti ${mileageConfirmed ? 'ti-check' : 'ti-gauge'}`} aria-hidden="true" /></span>
                            <div>
                                <p className="dg-card-title">Пробег при приёмке</p>
                                {!mileageConfirmed && <p className="dg-card-sub">Введите пробег и сфотографируйте одометр — после подтверждения откроется чек-лист</p>}
                            </div>
                        </div>
                        <div className="dg-input-wrap">
                            <input type="number" inputMode="numeric" className="dg-input" data-error={missingPhotos.includes('mileage')} value={mileageInput} onChange={e => { setMileageInput(e.target.value); setMileageConfirmed(false); setInspectionHints([]) }} onBlur={e => { handleSaveMileage(e.target.value).catch(err => alert(err.message || 'Не удалось сохранить пробег')) }} placeholder="Введите пробег (км)" />
                            <span>км</span>
                        </div>
                        <input type="file" accept="image/*" ref={mileagePhotoRef} style={{ display: 'none' }} onChange={async e => {
                            const file = e.target.files[0]
                            if (!file) return
                            setMileageConfirmed(false)
                            setInspectionHints([])
                            await uploadMileagePhoto(file, currentOrder.id)
                        }} />
                        <PhotoBox
                            onPreview={setPreviewPhoto}
                            missing={missingPhotos.includes('mileage_photo')}
                            title={missingPhotos.includes('mileage_photo') ? 'Необходимо прикрепить фото одометра' : 'Фото одометра (обязательно)'}
                            list={photos['15_none']}
                            alt="одометр"
                            onDelete={(photo, i) => handleDeletePhoto(15, null, photo.id, i)}
                            failed={failedUploads['15_none']}
                            onRetry={() => retryUpload('15_none')}
                            onGallery={() => { mileagePhotoRef.current?.removeAttribute('capture'); mileagePhotoRef.current?.click() }}
                            onCamera={() => { mileagePhotoRef.current?.setAttribute('capture', 'camera'); mileagePhotoRef.current?.click() }}
                        />
                        {!mileageConfirmed && (
                            <button type="button" className="dg-primary" onClick={confirmMileage} disabled={!mileageReady}>
                                Подтвердить пробег
                            </button>
                        )}
                        {mileageConfirmed && (
                            <div className="dg-confirmed">
                                <i className="ti ti-circle-check" aria-hidden="true" />
                                Пробег подтверждён
                            </div>
                        )}
                    </section>
                )}

                {isReadOnly && mileageInput && (
                    <section className="dg-card">
                        <div className="dg-card-head" style={{ marginBottom: '6px' }}>
                            <span className="dg-step" data-done="true"><i className="ti ti-gauge" aria-hidden="true" /></span>
                            <p className="dg-card-title" style={{ alignSelf: 'center' }}>Пробег при приёмке</p>
                        </div>
                        <p className="dg-mileage-value">{mileageInput} км</p>
                        {photos['15_none']?.length > 0 && (
                            <div className="dg-thumbs dg-thumbs-ro">
                                {photos['15_none'].map((photo, i) => (
                                    <div key={i} className="dg-thumb" style={{ width: '80px', height: '80px' }}>
                                        <img src={photo.url} alt="одометр" onClick={() => setPreviewPhoto(photo.url)} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {loadingHints && (
                    <div className="dg-hints-loading">
                        <div className="dg-hints-loading-row">
                            <div className="dg-hints-icon"><i className="ti ti-sparkles" aria-hidden="true" /></div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1A1A1A' }}>Подсказки AI</div>
                                <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>{currentOrder.car_brand} {currentOrder.car_model} · {mileageInput} км</div>
                            </div>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                {[0, 1, 2].map(i => (
                                    <div key={i} style={{
                                        width: '6px', height: '6px', borderRadius: '50%', background: '#0052FF',
                                        animation: 'vibePulse 1.2s ease-in-out infinite',
                                        animationDelay: `${i * 0.2}s`
                                    }} />
                                ))}
                            </div>
                        </div>
                        <div style={{ height: '3px', background: '#F3F4F6' }}>
                            <div style={{
                                height: '100%', width: '60%',
                                background: 'linear-gradient(90deg, #0052FF, #7C3AED)',
                                borderRadius: '0 2px 2px 0',
                                animation: 'vibePulse 1.5s ease-in-out infinite'
                            }} />
                        </div>
                    </div>
                )}

                {!loadingHints && <InspectionHints hints={inspectionHints} brand={currentOrder.car_brand} model={currentOrder.car_model} initiallyCollapsed={false} />}

                {!loadingHints && hintsError && (
                    <div className="dg-hints-error">
                        <i className="ti ti-alert-circle" aria-hidden="true" />
                        <span>{hintsError}. Чек-лист доступен, диагностику можно продолжить.</span>
                    </div>
                )}

                <OrderHistory orderId={currentOrder.id} token={token} refreshKey={`${currentOrder.status}-${currentOrder.mileage ?? ''}-${mileageConfirmed}-${Object.keys(results).length}`} />

                {!checklistUnlocked && !isReadOnly && (
                    <div className="dg-locked-note">
                        <i className="ti ti-lock" aria-hidden="true" />
                        Чек-лист откроется после подтверждения пробега
                    </div>
                )}

                {checklistGroups.map(group => {
                    const count = groupCounts(group)
                    return (
                        <div key={group} className="dg-group" data-locked={!checklistUnlocked} inert={!checklistUnlocked}>
                            <div className="dg-group-head">
                                <span className="dg-group-icon"><i className={`ti ${checklistUnlocked ? (groupIcons[group] || 'ti-list-check') : 'ti-lock'}`} aria-hidden="true" /></span>
                                <h2>{group}</h2>
                                {checklistUnlocked && count.total > 0 && <span className="dg-group-count" data-full={count.done >= count.total}>{count.done}/{count.total}</span>}
                            </div>
                            {items.filter(i => i.group_name === group).map(item => (
                                <div key={item.id} className="dg-item" data-state={itemState(item)} data-missing={itemMissing(item) ? 'true' : 'false'}>
                                    <p className="dg-item-title">{item.item_name}</p>
                                    {item.has_sides ? (
                                        <div className="dg-sides">
                                            {['left', 'right'].map(side => (
                                                <div key={side} className="dg-side">
                                                    <p className="dg-side-label"><i className={`ti ${side === 'left' ? 'ti-arrow-left' : 'ti-arrow-right'}`} aria-hidden="true" />{side === 'left' ? 'Левый' : 'Правый'}</p>
                                                    {statusButtons(item, side)}
                                                    {missingPhotos.includes(`${item.id}_${side}`) && !getStatus(item.id, side) && (
                                                        <div className="dg-need"><i className="ti ti-alert-triangle" aria-hidden="true" />Необходимо выбрать статус</div>
                                                    )}
                                                    {renderPhotoSection(item.id, side)}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div>
                                            {statusButtons(item, null)}
                                            {missingPhotos.includes(`${item.id}_none`) && !getStatus(item.id, 'none') && (
                                                <div className="dg-need"><i className="ti ti-alert-triangle" aria-hidden="true" />Необходимо выбрать статус</div>
                                            )}
                                            {renderPhotoSection(item.id, null)}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )
                })}

                <div className="dg-group" data-locked={!checklistUnlocked} inert={!checklistUnlocked}>
                    <div className="dg-group-head">
                        <span className="dg-group-icon"><i className={`ti ${checklistUnlocked ? 'ti-zoom-check' : 'ti-lock'}`} aria-hidden="true" /></span>
                        <h2>Дополнительный осмотр</h2>
                    </div>
                    <div className="dg-item">
                        <p className="dg-item-title" style={{ marginBottom: '10px' }}>Прочие элементы</p>
                        <input type="file" accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,image/*" ref={el => fileInputRefs.current['additional_none'] = el} style={{ display: 'none' }} onChange={async e => {
                            const file = e.target.files[0]
                            if (!file) return
                            e.target.value = ''
                            await uploadAdditionalPhoto(file, currentOrder.id)
                        }} />
                        <PhotoBox
                            onPreview={setPreviewPhoto}
                            title="Фото (необязательно)"
                            list={photos.additional_none}
                            alt="фото"
                            readOnly={isReadOnly}
                            onDelete={(photo, i) => handleDeletePhoto('additional', null, photo.id, i)}
                            failed={failedUploads.additional_none}
                            retryLabel={`${failedUploads.additional_none?.message} — повторить`}
                            onRetry={() => retryUpload('additional_none')}
                            onGallery={() => { fileInputRefs.current['additional_none']?.removeAttribute('capture'); fileInputRefs.current['additional_none']?.click() }}
                            onCamera={() => { fileInputRefs.current['additional_none']?.setAttribute('capture', 'camera'); fileInputRefs.current['additional_none']?.click() }}
                        />
                    </div>
                </div>
            </div>

            {!isReadOnly && (
                <div className="dg-wrap dg-conclusion">
                    <MechanicConclusion
                        value={summary}
                        onChange={setSummary}
                        unlocked={conclusionUnlocked}
                        lockMessage={checklistUnlocked
                            ? `Сначала выберите все статусы. Осталось: ${remainingStatuses}`
                            : 'Сначала подтвердите пробег и заполните все статусы'}
                        recording={recording}
                        completing={completingOrder}
                        uploading={hasUploadsInProgress}
                        onComplete={completeOrder}
                    />
                </div>
            )}
        </div>
    )
}
